import { MatomoProvider } from "@jonkoops/matomo-tracker-react";
import { useConnection } from "@solana/wallet-adapter-react";
import { enableBatching, enableCaching } from "@stepfinance/common";
import type { FC } from "react";
import { lazy, Suspense, useEffect, useMemo } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { BrowserRouter, matchPath, Navigate, Route, Routes as RouteList, useLocation } from "react-router-dom";

import { ComposeContextProviders } from "./components/ComposeContextProviders/ComposeContextProviders";
import { ErrorFallback, handleChunkFailure, isChunkFailedError } from "./components/ErrorFallback/ErrorFallback";
import { ConnectedWalletNotifier } from "./components/Notifications/ConnectedWalletNotifier";
import { QueryParamUtils } from "./components/QueryParamUtils/QueryParamUtils";
import { WalletModalProvider } from "./components/WalletModal/WalletModalProvider";
import { AccountsProvider } from "./contexts/accounts";
import { AppConfigProvider } from "./contexts/app/config";
import { ConcentratedLiquidityContextProvider } from "./contexts/concentratedLiquidity";
import { DexContextProvider } from "./contexts/dex";
import { DomainsProvider } from "./contexts/domains";
import { EnrichedWalletProvider } from "./contexts/enrichedWallet";
import { FarmProvider } from "./contexts/farm";
import { languageSources } from "./contexts/language/imports";
import { useLanguageContext } from "./contexts/language/language";
import { LendingContextProvider } from "./contexts/lending";
import { LiquidityProvider } from "./contexts/liquidity";
import { ManagedFundsContextProvider } from "./contexts/managedFunds";
import { MarginContextProvider } from "./contexts/margin";
import { MarketProvider } from "./contexts/market";
import { MarketDataMapProvider } from "./contexts/marketDataMap";
import { NavigationContextProvider } from "./contexts/navigation";
import { NFTContextProvider } from "./contexts/nft";
import { NFTMarketsContextProvider } from "./contexts/nftMarkets";
import { PortfolioOverviewContextProvider } from "./contexts/portfolioOverview";
import { RefreshProvider } from "./contexts/refresh";
import { RewardProvider } from "./contexts/reward";
import { SimplifiedViewProvider } from "./contexts/simplifiedView";
import { StakingContextProvider } from "./contexts/staking";
import { StarAtlasContextProvider } from "./contexts/starAtlas";
import { SwapContextProvider } from "./contexts/swap";
import { ValidatorStakingContextProvider } from "./contexts/validorStaking";
import { VaultsContextProvider } from "./contexts/vaults";
import { WalletContextProvider } from "./contexts/wallet";
import { XStepProvider } from "./contexts/xStep";
import { AppLayout } from "./layouts/AppLayout";
import { MATOMO_INSTANCE } from "./utils/matomo";
import { HomeView } from "./views";

interface WrappedConnectionProviderProps {
  children: React.ReactNode;
}

const WrappedConnectionProvider: FC<WrappedConnectionProviderProps> = ({ children }) => {
  const { connection } = useConnection();

  useEffect(() => {
    enableBatching(connection, 15, false); // set to false to remove logging
    enableCaching(connection, 2000, false); // set to false to remove logging
  }, [connection]);

  return <>{children}</>;
};

const Swap = lazy(() => import("./views/swap/SwapView"));
const Liquidity = lazy(() => import("./views/liquidity/LiquidityView"));
const Stake = lazy(() => import("./views/stake/index"));
const Unstake = lazy(() => import("./views/stake/index"));
const History = lazy(() => import("./views/history/index"));
const NFT = lazy(() => import("./views/nft/index"));
const Referrals = lazy(() => import("./views/referrals/index"));
const ReferrerInvite = lazy(() => import("./views/referrerInvite/index"));
const AccountTools = lazy(() => import("./views/tools/index"));
const RewardAdmin = lazy(() => import("./views/reward/index"));

const prefetchMap = [
  {
    path: "/swap",
    prefetchComponents: [() => import("./views/liquidity/LiquidityView")],
  },
];

interface RoutePrefetcherProps {
  children?: React.ReactNode;
}
const RoutePrefetcher: FC<RoutePrefetcherProps> = ({ children }) => {
  const location = useLocation();
  const prefetchConfig = prefetchMap.find(({ path }) => matchPath({ path }, location.pathname));

  useEffect(() => {
    if (prefetchConfig) {
      const id = setTimeout(() => {
        prefetchConfig.prefetchComponents.forEach((component) => {
          component().catch((error) => {
            if (isChunkFailedError(error)) {
              handleChunkFailure();
            }
            throw error;
          });
        });
      }, 2000);

      return () => {
        clearTimeout(id);
      };
    }
  }, [prefetchConfig]);

  return <>{children}</>;
};

// A custom hook that builds on useLocation to parse
// the query string for you.
// NOTE-TODO: This hook may be REMOVED once React Router V6 is implemented in the project.
const useQuery = () => {
  const { search } = useLocation();

  return useMemo(() => new URLSearchParams(search), [search]);
};

const HomeRedirect: FC = () => {
  const queryParams = useQuery();
  const referrerName = queryParams.get("ref");
  return referrerName ? <ReferrerInvite referrerCode={referrerName} /> : <Navigate to="dashboard" />;
};

export function Routes() {
  const ContextProviderList = useMemo(
    () => [
      NavigationContextProvider,
      AppConfigProvider,
      WalletContextProvider,
      EnrichedWalletProvider,
      WalletModalProvider,
      WrappedConnectionProvider,
      DomainsProvider,
      MarketDataMapProvider,
      XStepProvider,
      MarketProvider,
      DexContextProvider,
      LiquidityProvider,
      LendingContextProvider,
      AccountsProvider,
      ConcentratedLiquidityContextProvider,
      SwapContextProvider,
      ValidatorStakingContextProvider,
      FarmProvider,
      VaultsContextProvider,
      StakingContextProvider,
      MarginContextProvider,
      RefreshProvider,
      StarAtlasContextProvider,
      NFTContextProvider,
      RewardProvider,
      ManagedFundsContextProvider,
      NFTMarketsContextProvider,
      PortfolioOverviewContextProvider,
      SimplifiedViewProvider,
    ],
    [],
  );

  const { languageSetting } = useLanguageContext();

  const renderRoutes = () => {
    return (
      <>
        <Route index element={<HomeRedirect />} />
        <Route path="dashboard" element={<HomeView />} />
        <Route path="swap" element={<Swap />} />
        <Route path="liquidity" element={<Liquidity />}>
          <Route path="add" element={<Liquidity />} />
          <Route path="remove" element={<Liquidity />} />
        </Route>
        <Route path="stake" element={<Stake action="stake" />} />
        <Route path="unstake" element={<Unstake action="unstake" />} />
        <Route path="history">
          <Route path="latest" element={<History type="latest" />} />
          <Route path="calendar" element={<History type="calendar" />} />
        </Route>
        <Route path="account-tools" element={<AccountTools />} />
        <Route path="referrals">
          <Route index element={<Referrals />} />
          <Route path="invitation-example" element={<ReferrerInvite isExamplePage />} />
        </Route>
        <Route path="nft" element={<NFT />}>
          <Route path=":nftMint" element={<NFT />} />
        </Route>
        <Route path="reward-option-stats" element={<RewardAdmin />} />
      </>
    );
  };

  const languageBasedRoutes = () => {
    return Object.keys(languageSources).map((langCode, i) => {
      return (
        <Route key={i} path={langCode}>
          {renderRoutes()}
        </Route>
      );
    });
  };

  return (
    <>
      <MatomoProvider value={MATOMO_INSTANCE}>
        <BrowserRouter basename={"/"}>
          <RoutePrefetcher>
            {/* TODO clean this up */}
            {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
            {/* @ts-ignore */}
            <ErrorBoundary FallbackComponent={ErrorFallback}>
              <ComposeContextProviders components={ContextProviderList}>
                <ConnectedWalletNotifier />
                <AppLayout>
                  <Suspense fallback={null}>
                    <QueryParamUtils />
                    <RouteList>
                      <Route path="/" element={<Navigate to={languageSetting} replace />} />
                      {languageBasedRoutes()}
                    </RouteList>
                  </Suspense>
                </AppLayout>
              </ComposeContextProviders>
            </ErrorBoundary>
          </RoutePrefetcher>
        </BrowserRouter>
      </MatomoProvider>
    </>
  );
}
